// React
import React from 'react';
// External
import { Container, Form, Row, Col } from 'react-bootstrap';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { BiSearch } from 'react-icons/bi';
import isNil from 'lodash/isNil';
// Internal
import NavbarPrimary from 'components/shared/organisms/NavbarPrimary/NavbarPrimary';
import NavbarSecondary from 'components/shared/organisms/NavbarSecondary/NavbarSecondary';
import PageTitleWithCart from 'components/shared/molecules/PageTitleWithCart/PageTitleWithCart';
import ContentCard from 'components/shared/organisms/ContentCard/ContentCard';
import FooterBtnActions from 'components/shared/molecules/FooterBtnActions/FooterBtnActions';
import Stepper from 'components/shared/atoms/Stepper/Stepper';
import BookingSubmitted from 'components/new-booking/BookingSubmitted/BookingSubmitted';
import BookingDetails from 'components/new-booking/BookingDetails/BookingDetails';
import AlertDismissible from 'components/shared/atoms/AlertDismissible/AlertDismissible';
import { completeBooking } from 'services/booking';
import { updateBooking, clearBooking } from 'stores/actions';
import VetService from 'services/vet';
// Constants
import VET_SUPPLY_STEPS from 'constants/vetSupplySteps';
// Styles
import './styles.scss';
import ProductList from 'components/new-booking/ProductList/ProductList';
import Selector from 'components/shared/atoms/Selector/Selector';
import PRODUCT_CATEGORIES from 'constants/productCategories';
import PRODUCT_LIST_FILTERS from 'constants/productListFilters';
import PageTitle from 'components/shared/atoms/PageTitle/PageTitle';
import { logErrorToAtatus } from 'services/atatus';

const VET_SUPPLY_ORDER = 'vet supply order';
const DEFAULT_ERROR_MSG = "Something went wrong. Please verify the information and try again.";

class VetSupply extends React.Component {
    constructor(props) {
        super(props);
        const filterFromStorage = localStorage.getItem('product-filter') ?? '';
        const searchFromStorage = localStorage.getItem('product-search') ?? '';
        this.filters = this.getCategoryFilters();
        this.state = {
            headerTitle: '',
            steps: this.getStepsArray(),
            isSubmitted: false,
            bookingId: null,
            searchValue: searchFromStorage,
            isLoading: false,
            alertFail: {
                show: false,
                message: DEFAULT_ERROR_MSG,
            },
            filter: filterFromStorage !== '' ? filterFromStorage : this.filters[0].value,
            vetDetails: null,
            isUnsubmitted: true,
        };
        this.formComponent = React.createRef();
        this.bookingDetailsRef = React.createRef();
    }

    componentDidMount() {
        this.updateStepData();
        this.setInitialBookingData();
        this.getVetDetails();
        localStorage.setItem('service-type-name','vet supply order')
    }

    componentDidUpdate() {
        this.updateStepData();
    }

    /**
     * Gets the category filters
     * 
     * @returns {array}
     */
     getCategoryFilters() {
        const excludedFilters = ['Keepsakes', 'Urns & Vessels', 'Memorabilia'];

        let filteredFilters = PRODUCT_LIST_FILTERS.filter(filter => !excludedFilters.includes(filter.name));

        // Remove the first (all) filter if there is only 2 filters left
        if (filteredFilters.length <= 2) {
            filteredFilters.shift();
        }

        filteredFilters.push({
            name: PRODUCT_CATEGORIES.vetSupplies,
            value: PRODUCT_CATEGORIES.vetSupplies
        });

        return filteredFilters;
    }

    /**
     * Retrieves the vet details
     */
     getVetDetails = () => {
        VetService.getVetDetails()
            .then((response) => {
                this.setState({ vetDetails: response.data });
            }).catch((err) => {
                console.error(err);
            });
    }

    /**
     * Submit the form based on the async form component submit
     * @todo Add submit information request
     */
    async onSubmit() {
        try {
            // Gets the data about the current form step
            this.setState({ isLoading: true });
            const currentStepIndex = this.getCurrentStepIndex();
            const currentStepName = this.state.steps[currentStepIndex].name;

            if (this.state.isSubmitted) {
                this.props.history.push(`/booking/${this.state.bookingId}`);
            } else if (currentStepName === VET_SUPPLY_STEPS.productList.name) {
                // handles the submission of the form of the current step
                // based on the private cremation steps
                // If only selects items, the continues to the next page to confirm the booking
                // Verify there's at least one product in the booking
                this.navigateNext();
                this.setState({ isLoading: false });
            } else if (currentStepName === VET_SUPPLY_STEPS.confirmation.name && !this.state.isSubmitted) {
                const vetSupplyData = this.props.booking.submitVetSupply();
                try {
                    // Get the screenshot image of the booking details
                    const screenshot = await this.bookingDetailsRef.current.getImage();
                    await completeBooking(this.props.booking.id, {
                        screenshotImage: screenshot, 
                    });
                    this.setState({ isSubmitted: true, bookingId: this.props.booking.id });
                    this.props.clearBooking();
                    // remove temporary booking flag when booking is complete
                    await localStorage.removeItem('createBookingFlag')
                } catch (error) {
                    logErrorToAtatus("Failed to submit booking at Vet Supply Order", {
                        ...error, 
                        message: error.message, 
                        booking_id: this.props.booking.id, 
                        booking_number: this.props.booking.orderId
                    })

                    await this.setState({
                        alertFail: {
                            show: true,
                            message: "Error: " + (!isNil(error?.response?.data?.errors?.detail) ? error.response.data.errors.detail : DEFAULT_ERROR_MSG),
                        },
                        isLoading: false 
                    });
                } finally {
                    this.setState({ isLoading: false });
                }
            } else if (this.state.isSubmitted) {
                this.navigateTo('/bookings');
                await this.setState({ isLoading: false });
            }
        } catch (error) {
            logErrorToAtatus("Failed to submit booking at Vet Supply Order", {
                ...error, 
                message: error.message, 
                booking_id: this.props.booking.id, 
                booking_number: this.props.booking.orderId
            })
            
            this.setState({ isLoading: false });
        }
    }

    async setInitialBookingData() {
        // Set up booking
        const serviceType = await this.getServiceTypes();
        this.props.updateBooking({
            serviceType: serviceType.id
        });
    }

    /**
     * Retrieves the service types to display in the booking type selection
     * @returns The vet supply service object retrieved from sugar api
     */
    getServiceTypes = async () => {
        try {
            const response = await VetService.getServiceTypes();
            const serviceType = response.data.find((item) => VET_SUPPLY_ORDER === item.name.toLowerCase());
            return serviceType;
        } catch (error) {
            console.error(error);
        }
    }

    /**
     * @returns the current index step
     */
    getCurrentStepIndex() {
        const currentStepHash = this.props.location.hash;
        for (const [index, step] of this.state.steps.entries()) {
            if (step.hash === currentStepHash) {
                return index;
            }
        }
        return null;
    }

    getStepsArray = () => {
        return Object.keys(VET_SUPPLY_STEPS).map(
            (key) => VET_SUPPLY_STEPS[key]
        );
    }

    navigateTo(path) {
        this.props.history.push(path);
    }

    /**
     * Navigate to dashboard on cancel
     * @todo verify if you can leave the page
     */
    navigateCancel() {
        this.navigateTo('/dashboard');
    }

    /**
     * Handler to navigate to the next form after submitting the form,
     * it navigates to the next based on the current step
     */
    navigatePrev() {
        const currentStepHash = this.props.location.hash;
        let currentStepIndex = 0;
        this.state.steps.forEach((step, index) => {
            if (step.hash === currentStepHash) currentStepIndex = index;
        });
        if (currentStepIndex === 0) {
            // Navigates to new booking page if the current step is the initial
            this.navigateTo('/new-booking');
        } else if (currentStepIndex > 0) {
            // Navigates to the previous step
            const prevStep = currentStepIndex - 1;
            this.navigateTo(this.state.steps[prevStep].hash);
        }
    }

    /**
     * Handler to navigate to the next form after submitting the form,
     * it navigates to the next based on the current step
     */
    navigateNext() {
        const currentStepIndex = this.getCurrentStepIndex();
        const nextStep = currentStepIndex + 1;
        if (nextStep < this.state.steps.length) {
            this.navigateTo(this.state.steps[nextStep].hash);
        }
    }

    /**
     * Updates the data of the current step based on the constant variable of the new booking steps
     */
    updateStepData() {
        const stepHash = this.props.location.hash;

        // If the hash is not define then navigates to the first step
        if (!stepHash) this.navigateTo(VET_SUPPLY_STEPS.productList.hash);

        // Updates the information of the current step
        Object.keys(VET_SUPPLY_STEPS).forEach((key, index) => {
            if (VET_SUPPLY_STEPS[key].hash === stepHash) {
                const totalSteps = Object.keys(VET_SUPPLY_STEPS).length;
                const title = `Step ${index + 1} of ${totalSteps} - ${VET_SUPPLY_STEPS[key].title}`;

                // If the current headerTitle state has change then is updated
                if (this.state.headerTitle !== title) {
                    this.setState({ headerTitle: title });
                }
            }
        });
    }

    /**
     * Returns the form to be displayed on the current step of the new booking
     */
    headerSubcomponentView() {
        return null;
    }

    /**
     * Returns the form to be displayed on the current step of the new booking
     */
    stepFormView() {
        const stepHash = this.props.location.hash;
        switch (stepHash) {
            case VET_SUPPLY_STEPS.productList.hash:
                return (
                    <Row className="productOptions">
                        <Col className="product">
                            <Row className="filter-search-subheader custom-no-caret">
                                <Form inline>
                                    <Container>
                                        <Row>
                                            <Col>
                                                <h3 className='step-title'>{this.state.headerTitle}</h3>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col>
                                                <Form.Control
                                                    id="search"
                                                    placeholder="Search"
                                                    value={this.state.searchValue}
                                                    onChange={(event) => {
                                                        this.setState({
                                                            searchValue: event.target.value
                                                        }, () => {
                                                            localStorage.setItem('product-search', event.target.value);
                                                            const url = new URL(window.location.href);
                                                            url.searchParams.set('page', '1');
                                                            window.history.pushState(null, '', url.toString());
                                                        });
                                                    }}
                                                />
                                                <BiSearch size="18" />
                                            </Col>
                                            <Col>
                                                <Row>
                                                    <Col md={2} className="filter-label-col">
                                                        <span className="filter-label">Filter</span>
                                                    </Col>
                                                    <Col>
                                                        <Selector
                                                            options={this.filters}
                                                            name="filters"
                                                            onChange={(value) => {
                                                                const bFilterChanged = this.state.filter !== value;

                                                                this.setState({
                                                                    filter: value
                                                                }, () => {
                                                                    localStorage.setItem('product-filter', value);

                                                                    // Stop if the filter has not changed its value
                                                                    if (!bFilterChanged) {
                                                                        return;
                                                                    }

                                                                    // Get the query params from the url
                                                                    const queryParams = new URLSearchParams(this.props.location.search);
                                                                    const page = queryParams.get('page');

                                                                    // If the page is not set to 1, re-set it to 1 and reload the page
                                                                    if (page && page !== '1') {
                                                                        queryParams.set('page', 1);
                                                                        this.props.location.search = '?' + queryParams;
                                                                        this.props.history.push({
                                                                            pathname: this.props.location.pathname,
                                                                            search: this.props.location.search,
                                                                            hash: this.props.location.hash,
                                                                        });
                                                                        window.location.reload();
                                                                    }
                                                                });
                                                            }}
                                                            value={this.state.filter}
                                                        />
                                                    </Col>
                                                </Row>
                                            </Col>
                                        </Row>
                                    </Container>
                                </Form>
                            </Row>
                            <ProductList search={this.state.searchValue} filter={this.state.filter} filterOptions={this.filters} />
                        </Col>
                        <Col className='cart'>
                            {
                                <PageTitleWithCart
                                    title={`${this.state.isEdit ? 'Edit' : 'New'} private cremation`}
                                    items={this.props.booking.getQuotedProducts()}
                                    vetDetails={this.state.vetDetails}
                                    hasSelected={(selected) => this.setState({ hasSelectedProduct: selected })}
                                    bundleLists={this.state.bundleLists}
                                    style={{ paddingTop: 75 }}
                                />
                            }
                        </Col>
                    </Row>
                );
            case VET_SUPPLY_STEPS.confirmation.hash:
                return (
                    <div className="vet-supply-confirmation">
                        <BookingDetails booking={this.props.booking} vetDetails={this.state.vetDetails} ref={this.bookingDetailsRef} headerTitle={this.state.headerTitle}/>
                    </div>
                );
            default:
                return null;
        }
    }

    render() {
        return (
            <div className="main-container">
                <NavbarSecondary />
                <NavbarPrimary />
                <div className="body-content">
                    {
                        <PageTitle title="New vet supply order" />
                    }
                    {
                        this.props.location.hash && !this.state.isSubmitted
                            ? <Stepper steps={this.state.steps} />
                            : null
                    }
                    {
                        !this.state.isSubmitted
                            ? (
                                <ContentCard
                                    className="mt-28"
                                    headerTitle={this.state.headerTitle}
                                    headerStyle={{ paddingBottom: '10px' }}
                                    headerSubComponent={this.headerSubcomponentView()}
                                >
                                    <Container className={ "nb-container " + (this.props.location.hash === VET_SUPPLY_STEPS.confirmation.hash ? 'confirmation-page' : '')}>
                                        {this.stepFormView()}
                                    </Container>
                                </ContentCard>
                            ) : (
                                <BookingSubmitted bookingId={this.state.bookingId} vetDetails={this.state.vetDetails} isUnsubmitted={this.state.isUnsubmitted}/>
                            )
                    }
                </div>
                {
                    !this.state.isSubmitted
                        ? (
                            <FooterBtnActions
                                isEdit={this.state.isEdit}
                                currentPage={this.props.location.hash}
                                showSpinner={this.state.isLoading}
                                nextBtnText={
                                    this.props.location.hash === VET_SUPPLY_STEPS.confirmation.hash
                                        ? 'Submit booking'
                                        : 'Next'
                                }
                                cancelBtnOnCLick={() => this.navigateCancel()}
                                prevBtnOnCLick={() => this.navigatePrev()}
                                nextBtnOnCLick={() => this.onSubmit()}
                            />
                        ) : (
                            <FooterBtnActions
                                nextBtnText="View Booking"
                                nextBtnOnCLick={() => this.onSubmit()}
                            />
                        )
                }
                <AlertDismissible
                    show={this.state.alertFail.show}
                    type="danger"
                    text={this.state.alertFail.message}
                    onClose={() => this.setState({ 
                        alertFail: { 
                            show: false,
                            message: DEFAULT_ERROR_MSG,
                        }
                    })}
                />
            </div>
        );
    }
}

const mapStateToProps = (state) => ({ booking: state.booking });

const mapDispatchToProps = (dispatch) => {
    return {
        updateBooking: (bookingObj) => dispatch(updateBooking(bookingObj)),
        clearBooking: () => dispatch(clearBooking())
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(VetSupply));
